import cronstrue from "cronstrue";
import "cronstrue/locales/es";
import "cronstrue/locales/pt_BR";
import "cronstrue/locales/ja";
import "cronstrue/locales/de";
import "cronstrue/locales/fr";
import {normalizeLocaleLanguage} from "@/constants/localization";

// Schedule mode types
export type ScheduleMode = "interval" | "cron";

// Interval unit types
export type IntervalUnit = "day" | "week" | "month";

// Days of the week mapping
export const DAYS_OF_WEEK = [
  { key: "MON", label: "Mon", cronValue: "MON" },
  { key: "TUE", label: "Tue", cronValue: "TUE" },
  { key: "WED", label: "Wed", cronValue: "WED" },
  { key: "THU", label: "Thu", cronValue: "THU" },
  { key: "FRI", label: "Fri", cronValue: "FRI" },
  { key: "SAT", label: "Sat", cronValue: "SAT" },
  { key: "SUN", label: "Sun", cronValue: "SUN" },
] as const;

// Cron day values to our keys
const CRON_DAY_TO_KEY: Record<string, string> = {
  "0": "SUN",
  "7": "SUN",
  SUN: "SUN",
  "1": "MON",
  MON: "MON",
  "2": "TUE",
  TUE: "TUE",
  "3": "WED",
  WED: "WED",
  "4": "THU",
  THU: "THU",
  "5": "FRI",
  FRI: "FRI",
  "6": "SAT",
  SAT: "SAT",
};

// Common timezones to show at the top of the list
const COMMON_TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Sao_Paulo",
  "America/Mexico_City",
  "Europe/London",
  "Europe/Paris",
  "Asia/Tokyo",
  "Asia/Shanghai",
  "Australia/Sydney",
  "UTC",
];

// Get all IANA timezones with common ones at the top
export const ALL_TIMEZONE_OPTIONS = (() => {
  const allTimezones = Intl.supportedValuesOf("timeZone");
  const commonOptions = COMMON_TIMEZONES.filter(tz => allTimezones.includes(tz)).map(tz => ({
    value: tz,
    label: tz.replaceAll("_", " "),
  }));
  const otherTimezones = allTimezones
    .filter(tz => !COMMON_TIMEZONES.includes(tz))
    .map(tz => ({
      value: tz,
      label: tz.replaceAll("_", " "),
    }));
  return [...commonOptions, ...otherTimezones];
})();

// Interval unit options for dropdown
export const INTERVAL_UNIT_OPTIONS = [
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
] as const;

// Default timezone
export const DEFAULT_TIMEZONE = "America/New_York";

/**
 * Basic regex to validate cron expression format (5 fields)
 * This is a simple structural check, not a full cron validator.
 */
// TODO [SECURITY]: Consider using a dedicated cron validation library for
// stricter validation of cron expressions before processing.
const CRON_FORMAT_REGEX = /^(\S+\s+){4}\S+$/;

/**
 * Check if a string has valid cron expression format (5 space-separated fields)
 *
 * @param expression - The cron expression to validate
 * @returns True if the format is valid
 */
export function hasValidCronFormat(expression: string | undefined): boolean {
  if (!expression?.trim()) return false;
  return CRON_FORMAT_REGEX.test(expression.trim());
}

/**
 * Safely parse an integer with a fallback value
 *
 * @param value - String value to parse
 * @param fallback - Fallback value if parsing fails
 * @returns Parsed integer or fallback
 */
function safeParseInt(value: string, fallback: number): number {
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? fallback : parsed;
}

/**
 * Generate a cron expression from interval settings
 *
 * @param intervalValue - The interval value (e.g., every N days)
 * @param intervalUnit - The interval unit (day, week, month)
 * @param selectedDays - Selected days for weekly intervals
 * @param time - Time in HH:MM format
 * @returns Generated cron expression
 */
export function generateCronFromInterval(
  intervalValue: number,
  intervalUnit: IntervalUnit,
  selectedDays: string[],
  time: string
): string {
  const [hoursStr, minutesStr] = time.split(":");
  const h = safeParseInt(hoursStr, 9);
  const m = safeParseInt(minutesStr, 0);

  switch (intervalUnit) {
    case "day":
      if (intervalValue === 1) {
        return `${m} ${h} * * *`;
      }
      return `${m} ${h} */${intervalValue} * *`;
    case "week": {
      const days = selectedDays.length > 0 ? selectedDays.join(",") : "MON";
      return `${m} ${h} * * ${days}`;
    }
    case "month":
      if (intervalValue === 1) {
        return `${m} ${h} 1 * *`;
      }
      return `${m} ${h} 1 */${intervalValue} *`;
    default:
      return `${m} ${h} * * *`;
  }
}

// Parsed cron settings interface
export interface ParsedCronSettings {
  mode: ScheduleMode;
  intervalValue: number;
  intervalUnit: IntervalUnit;
  selectedDays: string[];
  time: string;
}

/**
 * Default cron settings used when parsing fails or input is empty
 */
const DEFAULT_CRON_SETTINGS: ParsedCronSettings = {
  mode: "interval",
  intervalValue: 1,
  intervalUnit: "week",
  selectedDays: ["MON"],
  time: "09:00",
};

/**
 * Parse days of week from cron expression field
 *
 * @param dayOfWeek - The day of week field from cron expression
 * @returns Array of day keys (e.g., ["MON", "TUE"])
 */
function parseDaysOfWeek(dayOfWeek: string): string[] {
  const dayParts = dayOfWeek.split(",");
  const days = dayParts
    .flatMap(d => {
      // Handle ranges like MON-FRI
      if (d.includes("-")) {
        const [start, end] = d.split("-");
        const startKey = CRON_DAY_TO_KEY[start.toUpperCase()];
        const endKey = CRON_DAY_TO_KEY[end.toUpperCase()];
        if (startKey && endKey) {
          const dayOrder = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
          const startIdx = dayOrder.indexOf(startKey);
          const endIdx = dayOrder.indexOf(endKey);
          if (startIdx !== -1 && endIdx !== -1) {
            const rangeDays: string[] = [];
            for (let i = startIdx; i <= endIdx; i++) {
              rangeDays.push(dayOrder[i]);
            }
            return rangeDays;
          }
        }
        return [];
      }
      return CRON_DAY_TO_KEY[d.toUpperCase()] || null;
    })
    .filter((d): d is string => d !== null);

  return days.length > 0 ? days : ["MON"];
}

/**
 * Format time from parsed hour and minute values
 *
 * @param hour - Cron hour field
 * @param minute - Cron minute field
 * @returns Formatted time string (HH:MM)
 */
function formatTimeFromCron(hour: string, minute: string): string {
  const h = safeParseInt(hour, 9);
  const m = safeParseInt(minute, 0);
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
}

/**
 * Parse a cron expression and extract interval settings
 *
 * @param cronExpression - The cron expression to parse
 * @returns Parsed settings including mode, interval, days, and time
 */
export function parseCronExpression(cronExpression: string): ParsedCronSettings {
  if (!cronExpression?.trim()) {
    return { ...DEFAULT_CRON_SETTINGS };
  }

  const parts = cronExpression.trim().split(/\s+/);
  if (parts.length < 5) {
    return { ...DEFAULT_CRON_SETTINGS, mode: "cron" };
  }

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;
  const time = formatTimeFromCron(hour, minute);

  // Detect interval unit and value
  let intervalUnit: IntervalUnit = "day";
  let intervalValue = 1;
  let selectedDays: string[] = ["MON"];

  // Check for weekly pattern (specific days of week, * for day of month and month)
  if (dayOfMonth === "*" && month === "*" && dayOfWeek !== "*") {
    intervalUnit = "week";
    selectedDays = parseDaysOfWeek(dayOfWeek);
    return { mode: "interval", intervalValue, intervalUnit, selectedDays, time };
  }

  // Check for monthly pattern (specific day of month, * for day of week)
  if (dayOfWeek === "*" && month !== "*") {
    intervalUnit = "month";
    // Check for */N pattern
    if (month.startsWith("*/")) {
      intervalValue = safeParseInt(month.slice(2), 1);
    }
    return { mode: "interval", intervalValue, intervalUnit, selectedDays, time };
  }

  // Check for daily pattern (* * * for day/month/dayOfWeek or */N for day)
  if (dayOfWeek === "*" && month === "*") {
    if (dayOfMonth === "*") {
      // Daily at specific time - intervalValue already 1
    } else if (dayOfMonth.startsWith("*/")) {
      intervalValue = safeParseInt(dayOfMonth.slice(2), 1);
    } else {
      // Specific day of month - treat as monthly
      intervalUnit = "month";
    }
    return { mode: "interval", intervalValue, intervalUnit, selectedDays, time };
  }

  // Default to cron mode if we can't parse it
  return { ...DEFAULT_CRON_SETTINGS, mode: "cron", time };
}

/**
 * Convert cron expression to human-readable description
 *
 * @param cronExpression - The cron expression to describe
 * @returns Human-readable description or null if invalid
 */
export function getCronDescription(cronExpression: string | undefined, language = "en"): string | null {
  const cron = cronExpression?.trim();
  if (!cron) return null;
  
  // Validate format before attempting to parse
  if (!hasValidCronFormat(cron)) return null;
  
  try {
    const locale = normalizeLocaleLanguage(language);
    return cronstrue.toString(cron, {locale: locale === "pt" ? "pt_BR" : locale});
  } catch {
    return null;
  }
}

/**
 * Check if a cron expression is valid.
 * Note: Empty/undefined expressions are considered valid (optional field).
 * Use hasValidCronFormat() if you need to check for a required, non-empty expression.
 *
 * @param cronExpression - The cron expression to validate
 * @returns True if valid or empty, false if malformed
 */
export function isCronExpressionValid(cronExpression: string | undefined): boolean {
  const cron = cronExpression?.trim();
  if (!cron) return true; // Empty is valid (optional field)
  
  // First check format
  if (!hasValidCronFormat(cron)) return false;
  
  try {
    cronstrue.toString(cron);
    return true;
  } catch {
    return false;
  }
}
